import React, { useState } from "react";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import { Kicker } from "./Components/common/Section";
import { PrimaryAnchor, SecondaryAnchor, PrimaryAction } from "./Components/common/Button";
import backgroundImage from "../images/Planet_Background.png";

const Page = styled.section`
  overflow: clip;
`;

const HeroSection = styled.div`
  position: relative;
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: clamp(7rem, 12vw, 10rem) 8vw 5rem;

  @media (max-width: 480px) {
    padding-left: 5vw;
    padding-right: 5vw;
  }
  background-image: linear-gradient(
      130deg,
      rgba(10, 10, 14, 0.92),
      rgba(10, 10, 14, 0.5)
    ),
    url(${backgroundImage});
  background-size: cover;
  background-position: center;
`;

const Card = styled.div`
  max-width: 820px;
  width: 100%;
  padding: clamp(2rem, 4vw, 3rem);
  border: 1px solid var(--ink-10);
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.04), rgba(255, 255, 255, 0.015)),
    radial-gradient(circle at top left, rgba(214, 179, 106, 0.06), transparent 24%);
  backdrop-filter: blur(12px);
`;

const Title = styled.h1`
  font-family: "DM Serif Display", serif;
  font-size: clamp(2.2rem, 4vw, 3.4rem);
  line-height: 1;
  margin-bottom: 18px;
`;

const Subtitle = styled.p`
  max-width: 52rem;
  color: var(--ink-78);
  line-height: 1.85;
  margin-bottom: 32px;
`;

const CTAGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 38px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const InquirySection = styled.div`
  margin-bottom: 36px;
`;

const InquiryHeading = styled.h2`
  font-size: 12px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  margin-bottom: 16px;
  color: var(--accent-2);
`;

const InquiryList = styled.div`
  display: grid;
  border-top: 1px solid var(--ink-10);
`;

const InquiryRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  gap: 20px;
  padding: 18px 0;
  border-bottom: 1px solid var(--ink-06);
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const InquiryTitle = styled.strong`
  font-size: 14px;
  color: var(--ink);
`;

const InquiryText = styled.p`
  color: var(--muted);
  line-height: 1.7;
  font-size: 14px;
`;

const FormSection = styled.div`
  margin-bottom: 32px;
`;

const FormHeading = styled.h2`
  font-size: 12px;
  letter-spacing: .18em;
  text-transform: uppercase;
  margin-bottom: 18px;
  color: var(--accent-2);
`;

const Form = styled.form`
  display: grid;
  gap: 16px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const FormField = styled.div`
  display: grid;
  gap: 6px;

  label {
    font-size: 11px;
    letter-spacing: .14em;
    text-transform: uppercase;
    color: var(--accent-2);
  }

  input, select, textarea {
    width: 100%;
    padding: 12px 14px;
    border: 1px solid var(--ink-12);
    background: rgba(255,255,255,.03);
    color: var(--ink);
    font-family: "Space Grotesk", sans-serif;
    font-size: 14px;
    transition: border-color .2s ease;

    &::placeholder { color: var(--muted); opacity: .6; }
    &:focus { outline: none; border-color: rgba(214,179,106,.5); box-shadow: 0 0 0 2px rgba(214,179,106,.25); }
  }

  select {
    appearance: none;
    cursor: pointer;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath d='M6 8L1 3h10z' fill='%23f3efe6' opacity='.5'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 14px center;
    padding-right: 36px;
  }

  textarea { resize: vertical; min-height: 100px; }
`;

const SubmitButton = styled(PrimaryAction)`justify-self: start;`;

const SuccessMessage = styled.div`
  padding: 18px 22px;
  border: 1px solid rgba(214,179,106,.35);
  background: rgba(214,179,106,.08);
  font-size: 14px;
  color: var(--ink);
  line-height: 1.7;
  strong { display: block; margin-bottom: 6px; color: var(--accent); font-size: 13px; letter-spacing: .1em; text-transform: uppercase; }
`;

const ErrorMessage = styled.div`
  padding: 14px 18px;
  border: 1px solid rgba(220,80,80,.3);
  background: rgba(220,80,80,.06);
  font-size: 13px;
  color: var(--ink-78);
  line-height: 1.6;
`;

const ResponseNote = styled.p`
  margin-bottom: 28px;
  padding: 14px 18px;
  border: 1px solid rgba(214,179,106,.2);
  background: rgba(214,179,106,.04);
  font-size: 13px;
  color: var(--accent-2);
  letter-spacing: .06em;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoBlock = styled.div`
  padding-top: 16px;
  border-top: 1px solid var(--ink-10);

  h3 {
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    margin-bottom: 10px;
    color: var(--accent-2);
  }

  p {
    margin: 0;
    color: var(--ink);
    line-height: 1.6;
    font-size: 14px;
  }
`;

const Contact = () => {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus("submitting");
    const form = e.currentTarget;
    const data = new FormData(form);
    try {
      const res = await fetch("https://formsubmit.co/ajax/efe@rmsociete.com", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: data,
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <Page>
      <Helmet>
        <title>Contact RMS — Get In Touch | Regime Music Societe</title>
        <meta
          name="description"
          content="Contact Regime Music Societe for collaborations, publishing inquiries, sync opportunities, or artist services. Based in Atlanta, GA."
        />
        <meta property="og:title" content="Contact — RMS" />
        <meta
          property="og:description"
          content="Reach out for publishing, catalog, sync, artist services, or partnership conversations."
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://rmsociete.com/images/REGIME_logo.svg" />
        <meta property="og:url" content="https://rmsociete.com/contact" />
        <meta property="og:image" content="https://rmsociete.com/images/REGIME_logo.svg" />
        <link rel="canonical" href="https://rmsociete.com/contact" />
        <meta
          name="keywords"
          content="contact, music publishing, sync, artist services, catalog development, RMS"
        />
      </Helmet>

      <HeroSection>
        <Card>
          <Kicker>Get in touch</Kicker>
          <Title>Contact Regime Music Societe</Title>
          <Subtitle>
            RMS works across publishing, release strategy, catalog development, sync,
            and brand-facing creative partnerships. If you are an artist, manager,
            label, publisher, or brand, reach out with context and our team will direct
            the inquiry to the right lead.
          </Subtitle>
          <CTAGroup>
            <PrimaryAnchor href="mailto:efe@rmsociete.com?subject=RMS%20Inquiry">
              Submit Inquiry
            </PrimaryAnchor>
            <SecondaryAnchor href="tel:+14047364228">
              Call Atlanta Office
            </SecondaryAnchor>
          </CTAGroup>

          <InquirySection id="publishing">
            <InquiryHeading>Inquiry categories</InquiryHeading>
            <InquiryList>
              <InquiryRow>
                <InquiryTitle>Publishing administration</InquiryTitle>
                <InquiryText>
                  Catalog setup, splits, rights management, registration, and
                  long-term publishing strategy.
                </InquiryText>
              </InquiryRow>
              <InquiryRow id="partnerships">
                <InquiryTitle>Brand &amp; sync partnerships</InquiryTitle>
                <InquiryText>
                  Campaign music, sonic branding, sync-ready compositions, and
                  cross-market collaborations.
                </InquiryText>
              </InquiryRow>
              <InquiryRow>
                <InquiryTitle>Artist services</InquiryTitle>
                <InquiryText>
                  Release planning, creative positioning, collaboration strategy,
                  and global market support.
                </InquiryText>
              </InquiryRow>
            </InquiryList>
          </InquirySection>

          <FormSection>
            <FormHeading>Send a message</FormHeading>
            {status === "success" ? (
              <SuccessMessage>
                <strong>Message sent</strong>
                Thank you for reaching out. Our team will respond within 1–2 business days.
              </SuccessMessage>
            ) : (
              <Form onSubmit={handleSubmit} aria-label="Contact form">
                <input type="hidden" name="_subject" value="RMS Website Inquiry" />
                <input type="hidden" name="_captcha" value="false" />
                <FormRow>
                  <FormField>
                    <label htmlFor="name">Name</label>
                    <input id="name" name="name" type="text" required aria-required="true" placeholder="Your name" />
                  </FormField>
                  <FormField>
                    <label htmlFor="email">Email</label>
                    <input id="email" name="email" type="email" required aria-required="true" placeholder="you@example.com" />
                  </FormField>
                </FormRow>
                <FormField>
                  <label htmlFor="type">Inquiry type</label>
                  <select id="type" name="type" required aria-required="true">
                    <option value="">Select an inquiry type</option>
                    <option value="Publishing administration">Publishing administration</option>
                    <option value="Brand & sync partnerships">Brand &amp; sync partnerships</option>
                    <option value="Artist services">Artist services</option>
                    <option value="Catalog / release strategy">Catalog / release strategy</option>
                    <option value="Other">Other</option>
                  </select>
                </FormField>
                <FormField>
                  <label htmlFor="message">Message</label>
                  <textarea id="message" name="message" rows={5} required aria-required="true" placeholder="Tell us about your project…" />
                </FormField>
                {status === "error" && (
                  <ErrorMessage>Something went wrong. Please try again or email us directly at efe@rmsociete.com.</ErrorMessage>
                )}
                <SubmitButton type="submit" disabled={status === "submitting"}>
                  {status === "submitting" ? "Sending…" : "Send message"}
                </SubmitButton>
              </Form>
            )}
          </FormSection>

          {status !== "success" && <ResponseNote>Typical response time: 1–2 business days.</ResponseNote>}

          <address style={{ fontStyle: "normal" }}>
          <InfoGrid>
            <InfoBlock>
              <h3>Office</h3>
              <p>3424 Peachtree Road NE Suite 2200</p>
              <p>Atlanta, GA 30326</p>
            </InfoBlock>
            <InfoBlock>
              <h3>Phone</h3>
              <p>(404) 736 4228</p>
            </InfoBlock>
            <InfoBlock>
              <h3>Email</h3>
              <p>efe@rmsociete.com</p>
            </InfoBlock>
            <InfoBlock>
              <h3>Office hours</h3>
              <p>Mon–Fri · 9:00 AM – 6:00 PM ET</p>
            </InfoBlock>
          </InfoGrid>
          </address>
        </Card>
      </HeroSection>
    </Page>
  );
};

export default Contact;
